import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  Cell,
  HtmlTable,
} from '../../../../../../../../../../CommonComponents/OPL/HtmlTable/HtmlTable';
import { Checkbox } from '../../../../../../../../../../CommonComponents/OPL/Checkbox/Checkbox';
import {
  BeneficiaryType,
  useAccountBeneficiariesSaveCheckMutation,
} from '../../../../../../api/useAccountBeneficiariesSaveCheckedMutation';
import { toast } from '../../../../../../../../../../CommonComponents/OPL/Toast/Toast';
import { Text } from '../../../../../../../../../../CommonComponents/OPL/Text/Text';
import { formatCurrency } from '../../../../../../../../../../CommonUtils/formatNumber';
import { IconButton } from '../../../../../../../../../../CommonComponents/OPL/IconButton/IconButton';
import { BeneficiariesData } from '../../../../useBeneficiaries';
import {
  OtherAssetsAssetDTO,
  OtherAssetsSimulatedDTO,
} from '../../../../../../api/useEstatePlanningExplanationQuery';
import { useIsPdf } from '../../../../../../../../../hooks/useIsPdf';
import { Loading } from '../../../../../../../../../../CommonComponents/OPL/Loading/Loading';
import { Box } from '../../../../../../../../../../CommonComponents/OPL/Box/Box';
import { formatDate } from '../../../../../../../../../../CommonUtils/formatDate';
import { BeneficiariesList } from '../../../BeneficiariesList/BeneficiariesList';
import { PDFSafeTableHeaderCaption } from '../../../../../../../../../../CommonComponents/OPL/HtmlTable/components/PDFSafeTableHeaderCaption/PDFSafeTableHeaderCaption';

interface Props {
  otherAsset: BeneficiariesData<OtherAssetsAssetDTO> | BeneficiariesData<OtherAssetsSimulatedDTO>;
  isSimulation: boolean;
  setSelectedAsset: (otherAsset: OtherAssetsAssetDTO | OtherAssetsSimulatedDTO) => void;
}

export const OtherAsset = ({ otherAsset, isSimulation, setSelectedAsset }: Props) => {
  const { items, allChecked } = otherAsset;

  const isPrint = useIsPdf();

  const {
    mutateAsync: saveCheck,
    isLoading: isSaving,
  } = useAccountBeneficiariesSaveCheckMutation();

  return (
    <HtmlTable mb="24">
      <table>
        <colgroup>
          <col width="1px" />
          <col width="20%" />
          <col width="20%" />
          <col width="20%" />
          <col width="20%" />
          <col width="20%" />
          <col width="1px" />
        </colgroup>

        <thead>
          <PDFSafeTableHeaderCaption colSpan={7}>
            <Box display="flex" alignItems="center" css="grid-gap: 8px">
              <Box>{items[0].TypeName}</Box>

              <AnimatePresence>
                {isSaving && (
                  <motion.span
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                  >
                    <Loading size="small" />
                  </motion.span>
                )}
              </AnimatePresence>
            </Box>
          </PDFSafeTableHeaderCaption>

          <tr>
            <th>
              <Checkbox
                disabled={isSaving}
                checked={allChecked}
                onChange={async ev => {
                  try {
                    await saveCheck({
                      type: BeneficiaryType.OtherAsset,
                      simulated: isSimulation,
                      states: items.map(item => ({
                        id: 'SimulatedAccountId' in item ? item.SimulatedAccountId : item.Id,
                        state: ev.target.checked,
                        ...(isSimulation
                          ? {
                              simulatedAccountType: item.Type,
                              entityId: 'EntityId' in item ? item.EntityId : null,
                            }
                          : null),
                      })),
                    });
                  } catch (err) {
                    console.error(err);

                    toast.error('Something went wrong. Please try again.');
                  }
                }}
              />
            </th>

            <th>
              <Text variant="smallCaps" textAlign="left">
                Name
              </Text>
            </th>

            <th>
              <Text variant="smallCaps" textAlign="right">
                Balance
              </Text>
            </th>

            <th>
              <Text variant="smallCaps" textAlign="left">
                Primary Beneficiary
              </Text>
            </th>
            <th>
              <Text variant="smallCaps" textAlign="left">
                Contingent Beneficiary
              </Text>
            </th>
            <th>
              <Text variant="smallCaps" textAlign="left">
                Last Updated
              </Text>
            </th>
            <th>&nbsp;</th>
          </tr>
        </thead>

        <tbody>
          {items.map(item => (
            <tr key={`${item.Id}-${'SimulatedAccountId' in item ? item.SimulatedAccountId : ''} `}>
              <Cell>
                <Checkbox
                  disabled={isSaving}
                  checked={item.IsChecked}
                  onChange={async ev => {
                    try {
                      await saveCheck({
                        type: BeneficiaryType.OtherAsset,
                        simulated: isSimulation,
                        states: [
                          {
                            id: 'SimulatedAccountId' in item ? item.SimulatedAccountId : item.Id,
                            state: ev.target.checked,
                            ...(isSimulation
                              ? {
                                  simulatedAccountType: item.Type,
                                  entityId: 'EntityId' in item ? item.EntityId : null,
                                }
                              : null),
                          },
                        ],
                      });
                    } catch (err) {
                      console.error(err);

                      toast.error('Something went wrong. Please try again.');
                    }
                  }}
                />
              </Cell>

              <Cell>{item.Name}</Cell>
              <Cell>
                <Text variant="tabular" textAlign="right">
                  {formatCurrency(item.Balance, { decimalPlaces: 2 })}
                </Text>
              </Cell>
              <Cell>
                <BeneficiariesList beneficiaries={item.PrimaryBeneficiaries} />
              </Cell>
              <Cell>
                <BeneficiariesList beneficiaries={item.ContingentBeneficiaries} />
              </Cell>
              <Cell>{item.LastUpdated ? formatDate(item.LastUpdated) : '-'}</Cell>
              <Cell>
                {!isPrint && (
                  <IconButton
                    variant="subtleInactive"
                    icon="far fa-pencil"
                    onClick={() => {
                      setSelectedAsset(item);
                    }}
                  />
                )}
              </Cell>
            </tr>
          ))}
        </tbody>
      </table>
    </HtmlTable>
  );
};
